import React, { useEffect, useState } from 'react';
import { Box, ButtonV2, DrawerV2, Flex, TextV2, TooltipV2 } from '@withjoy/joykit';
import { CheckOutline, Close } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { boxShadowValuesV2 } from '@shared/utils/style/dropShadows';
import { pxToRem } from '@withjoy/joykit/theme';
import { useIsMounted } from '@shared/utils/hooks/useIsMounted';
import { useTranslation } from '@shared/core';
import { drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { useAdminAccommodationsTelemetry } from '../../Accommodations.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useMount } from '@shared/utils/hooks/useMount';

interface AutoPopulatedMoreHotelsTooltipContentProps
  extends Readonly<{
    onCloseDialog: () => void;
    onShowHotelPicks: () => void;
  }> {}

const AutoPopulatedMoreHotelsTooltipContent = (props: AutoPopulatedMoreHotelsTooltipContentProps) => {
  const { onCloseDialog, onShowHotelPicks } = props;

  const { tq } = useTranslation('travelMap');
  const telemetry = useAdminAccommodationsTelemetry();

  const handleShowHotelPicks = useEventCallback(() => {
    telemetry.roomBlockRatesDialogShowHotelPicks();
    onShowHotelPicks();
  });

  const handleCloseDialog = useEventCallback(() => {
    telemetry.onboardingWelcome.enter('Exited Tour');
    onCloseDialog();
  });

  useMount(() => {
    telemetry.onboardingWelcome.enter('Tour Relaunched');
  });

  return (
    <Box
      padding={7}
      display="flex"
      flexDirection="column"
      gap={6}
      position="relative"
    >
      {/* Close button */}
      <ButtonV2
        variant="ghost"
        intent="neutral"
        onClick={handleCloseDialog}
        aria-label={tq`Close Auto Populated More Hotels Tooltip`}
        padding={0}
        width={pxToRem(16)}
        minWidth={pxToRem(16)}
        height={pxToRem(16)}
        position="absolute"
        top={pxToRem(16)}
        right={pxToRem(16)}
      >
        <Close size={16} />
      </ButtonV2>

      {/* Content */}
      <Flex
        flexDirection="column"
        gap={6}
      >
        {/* Heading */}
        <TextV2
          typographyVariant="hed4"
          color="mono14"
        >{tq`We added more hotel suggestions near your new venue.`}</TextV2>

        {/* Bullet points */}
        <Flex
          flexDirection="column"
          gap={4}
        >
          <Flex
            gap={3}
            alignItems="center"
          >
            <CheckOutline
              size={24}
              color="mono14"
            />
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >{tq`Share guest stays, up to 40% with Joy.`}</TextV2>
          </Flex>

          <Flex
            gap={pxToRem(8)}
            alignItems="center"
          >
            <CheckOutline
              size={24}
              color="mono14"
            />
            <TextV2
              typographyVariant="body1"
              color="mono14"
            >{tq`Curate your list the way you want.`}</TextV2>
          </Flex>
        </Flex>

        {/* Button */}
        <ButtonV2
          variant="solid"
          intent="primary"
          shape="rounded"
          fullWidth
          onClick={handleShowHotelPicks}
        >
          {tq`Show Hotel Picks`}
        </ButtonV2>
      </Flex>
    </Box>
  );
};

interface AutoPopulatedMoreHotelsTooltipProps
  extends Readonly<{
    isOpen: boolean;
    onCloseDialog: () => void;
    onShowHotelPicks: () => void;
  }> {}

export const AutoPopulatedMoreHotelsTooltip = (props: AutoPopulatedMoreHotelsTooltipProps) => {
  const { isOpen, onCloseDialog, onShowHotelPicks } = props;

  const isMounted = useIsMounted();
  const isMobileOrTablet = useIsMobileOrTablet();
  const [isOpenAutoPopulationMoreHotelsTooltip, setIsOpenAutoPopulationMoreHotelsTooltip] = useState(false);

  // Delay opening PopoverV2 until after anchor is positioned
  // This ensures Popper reads correct anchor position on first render, prevent rendering before data is loaded
  useEffect(() => {
    if (!isMounted) return;
    const rafId = requestAnimationFrame(() => {
      setIsOpenAutoPopulationMoreHotelsTooltip(true);
    });
    return () => cancelAnimationFrame(rafId);
  }, [isMounted]); // No need for dialogIsOpen if it's constant
  if (!isOpen) return null;

  return (
    <>
      {isMobileOrTablet ? (
        <DrawerV2
          disableAutoFocus
          enableReturnFocusOnClose
          useBackdrop={false}
          overrides={drawerOverrides}
          isOpen={isOpenAutoPopulationMoreHotelsTooltip}
          anchor="bottom"
        >
          <AutoPopulatedMoreHotelsTooltipContent
            onCloseDialog={onCloseDialog}
            onShowHotelPicks={onShowHotelPicks}
          />
        </DrawerV2>
      ) : (
        <TooltipV2
          isOpen={isOpenAutoPopulationMoreHotelsTooltip}
          onClose={onCloseDialog}
          triggerType="none"
          placement="right"
          showArrow={true}
          showBackdrop={true}
          popperOptions={{
            disableFlip: true,
          }}
          overrides={{
            Content: {
              props: {
                backgroundColor: 'white',
                borderRadius: pxToRem(16),
                boxShadow: boxShadowValuesV2.xxxl,
                maxWidth: pxToRem(397),
                padding: 0,
              },
            },
            Arrow: {
              props: {
                backgroundColor: 'white',
              },
            },
          }}
          content={
            <AutoPopulatedMoreHotelsTooltipContent
              onCloseDialog={onCloseDialog}
              onShowHotelPicks={onShowHotelPicks}
            />
          }
        >
          <Box
            width={1}
            height={1}
            backgroundColor="transparent"
            position="fixed"
            right={9}
            top={pxToRem(340)}
          />
        </TooltipV2>
      )}
    </>
  );
};
